import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  AlertTriangle,
  Boxes,
  Building2,
  Check,
  ChevronRight,
  Info,
  Network,
  Server,
  ShieldAlert,
} from 'lucide-react';
import { api } from '../api/cliente.js';
import { Pill } from '../componentes/ui/Pill.js';

interface Resumo {
  kpis: {
    enderecosV4: number;
    blocosV6: number;
    operadoras: number;
    ptrsConfigurados: number;
    v4SemReverso: number;
  };
  inventario: {
    blocosV4: number;
    blocosV6: number;
    zonasV4: number;
    zonasV6: number;
    zonasPendentes: number;
    blocosClassless: number;
    fcrdnsConfere: number;
    fcrdnsDivergente: number;
    fcrdnsNaoVerificado: number;
  };
  cluster: {
    estado: string;
    tamanho: number;
    aceitaEscrita: boolean;
    nos: { nome: string; papel: string; status: string; ipMalha: string }[];
  };
  alertas: { nivel: 'erro' | 'aviso' | 'info'; titulo: string; detalhe: string; link?: string }[];
  atividade: { ts: string; usuario: string | null; acao: string; alvo: string }[];
}

const n = (v: number) => v.toLocaleString('pt-BR');

export function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Resumo>('/dashboard'),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return <Esqueleto />;

  const { kpis, inventario, cluster, alertas, atividade } = data;
  const cobertura =
    kpis.enderecosV4 > 0 ? Math.round((kpis.ptrsConfigurados / kpis.enderecosV4) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* ---------------------------------------------------------------- KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          rotulo="Endereços IPv4"
          valor={n(kpis.enderecosV4)}
          nota={`${inventario.blocosV4} blocos`}
          Icone={Network}
          tom="info"
        />
        {/* IPv6 aparece como blocos, e nunca como contagem de endereços: um
            único /48 tem 2^80, e somá-lo tornaria o número IPv4 invisível. */}
        <Kpi
          rotulo="Blocos IPv6"
          valor={n(kpis.blocosV6)}
          nota={`${inventario.zonasV6} zonas ip6.arpa`}
          Icone={Boxes}
          tom="ipv6"
        />
        <Kpi
          rotulo="Reversos configurados"
          valor={n(kpis.ptrsConfigurados)}
          nota={`${cobertura}% do inventário IPv4`}
          Icone={Check}
          tom="ok"
        />
        <Kpi
          rotulo="Operadoras"
          valor={n(kpis.operadoras)}
          nota={`${inventario.zonasV4 + inventario.zonasV6} zonas no total`}
          Icone={Building2}
          tom="neutro"
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          {/* ------------------------------------------------------ FCrDNS */}
          <Cartao titulo="Validação FCrDNS" subtitulo="Reverso confirmado pelo direto">
            {kpis.ptrsConfigurados === 0 ? (
              <p className="py-6 text-center text-[13px] text-texto-suave">
                Nenhum reverso configurado ainda.
              </p>
            ) : (
              <>
                <div className="flex h-2.5 overflow-hidden rounded-pill bg-borda-suave">
                  <Faixa valor={inventario.fcrdnsConfere} total={kpis.ptrsConfigurados} cor="bg-barra-ok" />
                  <Faixa valor={inventario.fcrdnsDivergente} total={kpis.ptrsConfigurados} cor="bg-barra-atencao" />
                  <Faixa valor={inventario.fcrdnsNaoVerificado} total={kpis.ptrsConfigurados} cor="bg-borda" />
                </div>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px]">
                  <Legenda cor="bg-barra-ok" rotulo="Confere" valor={inventario.fcrdnsConfere} />
                  <Legenda cor="bg-barra-atencao" rotulo="Divergente" valor={inventario.fcrdnsDivergente} />
                  <Legenda cor="bg-borda" rotulo="Não verificado" valor={inventario.fcrdnsNaoVerificado} />
                </div>
              </>
            )}
          </Cartao>

          {/* ------------------------------------------------------ atividade */}
          <Cartao titulo="Atividade recente" subtitulo="Últimas alterações registradas">
            {atividade.length === 0 ? (
              <p className="py-6 text-center text-[13px] text-texto-suave">
                Nenhuma alteração registrada ainda.
              </p>
            ) : (
              <ul className="space-y-3">
                {atividade.map((a, i) => (
                  <li key={i} className="flex gap-3 text-[13px]">
                    <span
                      className={`mt-1.5 size-2 shrink-0 rounded-full ${corDaAcao(a.acao)}`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate">
                        <span className="font-semibold">{a.usuario ?? 'Sistema'}</span>{' '}
                        <span className="text-texto-suave">{descrever(a.acao)}</span>{' '}
                        <span className="font-mono text-[12.5px]">{a.alvo}</span>
                      </p>
                      <p className="text-[11.5px] text-texto-mudo">{tempoRelativo(a.ts)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
        </div>

        <div className="space-y-5">
          {/* ------------------------------------------------------ cluster */}
          <Cartao titulo="Cluster" subtitulo={`${cluster.tamanho} membros`}>
            <div
              className={`mb-3 flex items-center gap-2 rounded-chip px-3 py-2 text-[12.5px] font-bold ${
                cluster.aceitaEscrita ? 'bg-sucesso-fundo text-sucesso' : 'bg-erro-fundo text-erro'
              }`}
            >
              {cluster.aceitaEscrita ? <Check size={15} /> : <ShieldAlert size={15} />}
              {cluster.aceitaEscrita ? 'Operando normalmente' : 'Somente leitura'}
            </div>
            <ul className="space-y-2">
              {cluster.nos.map((no) => (
                <li key={no.nome} className="flex items-center gap-2.5 text-[13px]">
                  <Server size={15} className="shrink-0 text-texto-mudo" />
                  <span className="flex-1 truncate font-semibold">{no.nome}</span>
                  <Pill tom={no.papel === 'ARBITRO' ? 'neutro' : 'info'}>
                    {no.papel === 'ARBITRO' ? 'árbitro' : 'dados'}
                  </Pill>
                </li>
              ))}
            </ul>
          </Cartao>

          {/* ------------------------------------------------------ alertas */}
          <Cartao titulo="Alertas" subtitulo={alertas.length === 0 ? 'Nada pendente' : undefined}>
            {alertas.length === 0 ? (
              <p className="py-4 text-center text-[13px] text-texto-suave">
                Nenhum problema detectado.
              </p>
            ) : (
              <ul className="space-y-3">
                {alertas.map((a, i) => {
                  const Icone = a.nivel === 'erro' ? AlertTriangle : a.nivel === 'aviso' ? AlertTriangle : Info;
                  const cor =
                    a.nivel === 'erro'
                      ? 'bg-erro-fundo text-erro'
                      : a.nivel === 'aviso'
                        ? 'bg-aviso-fundo text-aviso'
                        : 'bg-info-fundo text-info';
                  const conteudo = (
                    <>
                      <span className={`mt-0.5 grid size-7 shrink-0 place-items-center rounded-chip ${cor}`}>
                        <Icone size={14} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-bold leading-snug">{a.titulo}</p>
                        <p className="mt-0.5 text-[12px] leading-snug text-texto-suave">{a.detalhe}</p>
                      </div>
                      {a.link && (
                        <ChevronRight size={16} className="mt-0.5 shrink-0 self-center text-texto-mudo" />
                      )}
                    </>
                  );
                  // Alerta com link vira atalho para a tela da divergência.
                  return a.link ? (
                    <li key={i}>
                      <Link
                        to={a.link}
                        className="-mx-2 flex items-center gap-2.5 rounded-chip px-2 py-1.5 hover:bg-borda-tenue"
                      >
                        {conteudo}
                      </Link>
                    </li>
                  ) : (
                    <li key={i} className="flex gap-2.5">
                      {conteudo}
                    </li>
                  );
                })}
              </ul>
            )}
          </Cartao>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- auxiliares

function Kpi({
  rotulo,
  valor,
  nota,
  Icone,
  tom,
}: {
  rotulo: string;
  valor: string;
  nota: string;
  Icone: typeof Network;
  tom: 'info' | 'ok' | 'ipv6' | 'neutro';
}) {
  const cores = {
    info: 'bg-info-fundo text-info',
    ok: 'bg-sucesso-fundo text-sucesso',
    ipv6: 'bg-ipv6-fundo text-ipv6',
    neutro: 'bg-borda-suave text-texto-suave',
  };

  return (
    <div className="rounded-card border border-borda bg-superficie p-5 transition-shadow hover:shadow-hover">
      <div className="flex items-start justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
          {rotulo}
        </p>
        <span className={`grid size-9 place-items-center rounded-chip ${cores[tom]}`}>
          <Icone size={18} />
        </span>
      </div>
      {/* Mono e tabular: números grandes lado a lado precisam alinhar. */}
      <p className="mt-3 font-mono text-[28px] font-extrabold leading-none">{valor}</p>
      <p className="mt-1.5 text-[12.5px] text-texto-suave">{nota}</p>
    </div>
  );
}

function Cartao({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-card border border-borda bg-superficie p-5">
      <header className="mb-4">
        <h2 className="text-[15px] font-extrabold leading-tight">{titulo}</h2>
        {subtitulo && <p className="text-[12.5px] text-texto-suave">{subtitulo}</p>}
      </header>
      {children}
    </section>
  );
}

function Faixa({ valor, total, cor }: { valor: number; total: number; cor: string }) {
  if (valor === 0) return null;
  return <div className={cor} style={{ width: `${(valor / total) * 100}%` }} />;
}

function Legenda({ cor, rotulo, valor }: { cor: string; rotulo: string; valor: number }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`size-2.5 rounded-sm ${cor}`} aria-hidden="true" />
      <span className="text-texto-suave">{rotulo}</span>
      <span className="font-mono font-bold">{n(valor)}</span>
    </span>
  );
}

function corDaAcao(acao: string): string {
  if (acao.startsWith('ptr')) return 'bg-marca';
  if (acao.startsWith('bloco')) return 'bg-ipv6';
  if (acao.startsWith('operadora')) return 'bg-barra-ok';
  return 'bg-texto-mudo';
}

function descrever(acao: string): string {
  const mapa: Record<string, string> = {
    'ptr.definir': 'alterou o reverso de',
    'ptr.remover': 'removeu o reverso de',
    'ptr.massa': 'aplicou máscara em massa em',
    'bloco.criar': 'cadastrou o bloco',
    'operadora.criar': 'cadastrou a operadora',
  };
  return mapa[acao] ?? acao;
}

function tempoRelativo(iso: string): string {
  const segundos = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (segundos < 60) return 'agora mesmo';
  if (segundos < 3600) return `há ${Math.floor(segundos / 60)} min`;
  if (segundos < 86400) return `há ${Math.floor(segundos / 3600)} h`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

function Esqueleto() {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[124px] animate-pulse rounded-card border border-borda bg-superficie" />
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="h-64 animate-pulse rounded-card border border-borda bg-superficie" />
        <div className="h-64 animate-pulse rounded-card border border-borda bg-superficie" />
      </div>
    </div>
  );
}
